import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { cn, CountBadge, toPersianDigits } from '@platform/ui-kit'
import { useAuth } from '@/app/providers/AuthProvider'
import { MODULES, ICONS } from '@/app/routes/moduleRegistry'
import type { ModuleChild, ModuleDefinition } from '@/app/routes/moduleRegistry'
import { useChatUnread } from '@/features/chat/unreadStore'

/* ------------------------------------------------------------------
   Sidebar — سند مادر بند ۱۱.۱
   ------------------------------------------------------------------
   «منو بر اساس Permission تولید می‌شود؛ نبودن منو جایگزین کنترل
    Backend نیست.»

   منو دو سطحی است: ماژول ← بخش‌های داخلی. ماژول جاری خودکار باز
   می‌شود و کاربر می‌تواند بقیه را دستی باز و بسته کند.

   ماژول‌های مهاجرت‌نکرده هم با بخش‌هایشان دیده می‌شوند اما غیرفعال،
   تا کاربر بداند دقیقاً چه چیزی در راه است — نه فقط یک نام کلی.
   ------------------------------------------------------------------ */

interface SidebarProps {
  open: boolean
  onClose: () => void
  inboxCount: number
}

export function Sidebar({ open, onClose, inboxCount }: SidebarProps) {
  const { hasModuleAccess, canSeeModule, can } = useAuth()
  const location = useLocation()
  const chatUnread = useChatUnread()

  const activePath = location.pathname.split('/')[2] ?? ''
  const activeModule = MODULES.find((m) => m.path === activePath)
  const activeKey = activeModule?.key

  const [expanded, setExpanded] = useState<string[]>([])
  const [modulesOpen, setModulesOpen] = useState(true)

  /* در دسکتاپ منو همیشه باز است، پس نه inert می‌شود و نه با Escape
     بسته می‌شود. نقطه‌شکست از همان ۶۴rem کلاس‌های lg: خوانده می‌شود تا
     رفتار جاوااسکریپت و CSS از هم جدا نیفتند — همان الگوی قفل اسکرول
     در AppShell. */
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 64rem)').matches,
  )

  useEffect(() => {
    const query = window.matchMedia('(min-width: 64rem)')
    const sync = () => setIsDesktop(query.matches)
    sync()
    query.addEventListener('change', sync)
    return () => query.removeEventListener('change', sync)
  }, [])

  /* بستن کشو با Escape — رفتاری که کاربر از هر لایهٔ روی صفحه انتظار
     دارد. لمس بیرون از منو از قبل کار می‌کرد ولی کیبورد راهی برای
     بستن نداشت. */
  useEffect(() => {
    if (!open || isDesktop) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, isDesktop, onClose])

  /* ماژول جاری همیشه باز باشد — بدون بستن آنچه کاربر خودش باز کرده.
     اگر کاربر کل گروه ماژول‌ها را بسته باشد و به ماژولی برود، گروه
     دوباره باز می‌شود؛ وگرنه آیتم فعال پنهان می‌ماند.

     افکت فقط به activeKey وابسته است، پس جابه‌جایی بین بخش‌های همان
     ماژول دوباره اجرا نمی‌شود و انتخاب کاربر را به هم نمی‌زند. */
  useEffect(() => {
    if (!activeKey) return
    setExpanded((current) => (current.includes(activeKey) ? current : [...current, activeKey]))
    setModulesOpen(true)
  }, [activeKey])

  const toggle = (key: string) =>
    setExpanded((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    )

  /* ماژول‌های «فقط مدیر سیستم» برای بقیه اصلاً ساخته نمی‌شوند — نه
     خاکستری و نه با قفل. کاربری که هرگز قرار نیست واردشان شود، نباید
     دکمه‌شان را ببیند. */
  const modules = [...MODULES]
    .filter((module) => canSeeModule(module))
    .sort((a, b) => a.order - b.order)

  return (
    <>
      {/*
        در این الگو پرده‌ی تیره‌ای روی صفحه نمی‌افتد: محتوا کنار می‌رود و
        خودِ جابه‌جایی نشان می‌دهد که منو باز است، پس تیره کردن صفحه فقط
        خواندن محتوای کنار رفته را سخت می‌کند.

        اما لایه‌ی نامرئی لازم است تا لمس بیرون از منو آن را ببندد — رفتار
        استانداردی که کاربر از کشو انتظار دارد. چون شفاف است، محتوا کاملاً
        روشن می‌ماند.
      */}
      {open && (
        <div
          className="fixed inset-y-0 start-[var(--pf-sidebar-w)] end-0 lg:hidden"
          style={{ zIndex: 'var(--pf-z-overlay)' }}
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        aria-label="منوی اصلی"
        /* کشوی بسته فقط با transform بیرون قاب می‌رود؛ یعنی هنوز در
           جریان فوکوس و صفحه‌خوان است. بدون این، کاربر کیبورد در موبایل
           با Tab وارد منویی می‌شد که اصلاً نمی‌دیدش و فوکوس ناپدید
           می‌شد.

           ⚠️ شرط lg لازم است: در دسکتاپ منو همیشه باز و قابل استفاده
           است، پس آنجا نباید پنهان شود. */
        inert={!open && !isDesktop}
        aria-hidden={!open && !isDesktop ? true : undefined}
        className={cn(
          'flex w-[var(--pf-sidebar-w)] flex-col',
          /* Nova v2: ناوبری تیره و بدون حاشیه روشن */
          'bg-[var(--pf-nav-bg)]',
          /* موبایل — کشوی هُل‌دهنده: سایدبار چسبیده به لبه راست و درست
             بیرون قاب پارک می‌شود، و همراه با ستون محتوا به همان اندازه
             به چپ می‌لغزد. پس هر دو با هم حرکت می‌کنند و سایدبار جای
             خالیِ سمت راست را پر می‌کند، به‌جای اینکه روی محتوا بیفتد.

             ⚠️ سایدبار حتماً باید خودش هم انیمیشن بخورد: با start-0
             تنها، بیرونِ لبه می‌ماند و هرگز وارد قاب نمی‌شود.

             ⚠️ دو کلاسِ translate باید با شرط سه‌تایی جابه‌جا شوند، نه
             اینکه یکی روی دیگری گذاشته شود: هم‌وزن‌اند و در خروجی
             Tailwind کلاس full بعد از 0 می‌آید، پس همیشه full برنده
             می‌شد و کشو هرگز باز نمی‌شد.

             ⚠️ inset-y-0 اینجا لازم است وگرنه ارتفاع کشو صفر می‌شود و
             آیتم‌ها اصلاً رندر نمی‌شوند. */
          'fixed inset-y-0 start-0',
          'transition-transform duration-[var(--pf-duration)] ease-[var(--pf-ease)]',
          open ? 'translate-x-0' : 'translate-x-full',
          /* دسکتاپ — سایدبار همیشه هست و باید در جریان flex والد بنشیند،
             نه بیرون از صفحه. sticky چون خودِ صفحه اسکرول می‌شود: بدون
             آن سایدبار با اسکرول از بالا کنار می‌رفت و زیرش فضای خالی
             می‌ماند. با sticky + h-dvh همیشه تمام ارتفاع نمایشگر را پر
             می‌کند و منوی بلند داخل خودش اسکرول می‌شود.

             ⚠️ lg:translate-x-0 حتماً لازم است: بدون آن جابه‌جاییِ حالت
             موبایل باقی می‌ماند و سایدبار در دسکتاپ بیرون قاب می‌افتد.

             🔴 lg:self-start حیاتی است — نبودنش همان باگِ «فوتر با
             اسکرول بالا می‌ماند» را می‌ساخت.

             والد `flex min-h-dvh` است و align-items پیش‌فرضش stretch؛
             پس جعبهٔ سایدبار تا تهِ *کل صفحه* کش می‌آمد، نه یک نمایشگر.
             sticky اما فقط داخل جعبهٔ خودِ عنصر می‌چسبد: وقتی جعبه از
             همان اول تمام ارتفاع اسکرول را گرفته، top-0 هیچ‌وقت فعال
             نمی‌شود و سایدبار ساده با صفحه بالا می‌رود. h-dvh هم نجاتش
             نمی‌داد، چون ارتفاع را روی خودِ عنصر می‌گذاشت ولی جعبهٔ
             flex همچنان کشیده می‌ماند و فوتر ته آن جعبه می‌نشست.

             با self-start جعبه به اندازهٔ محتوای خودش جمع می‌شود،
             h-dvh یعنی دقیقاً یک نمایشگر، و sticky واقعاً می‌چسبد. */
          'lg:sticky lg:top-0 lg:start-auto lg:h-dvh lg:shrink-0 lg:translate-x-0 lg:self-start',
        )}
        style={{ zIndex: 'var(--pf-z-sidebar)' }}
      >
        <div className="flex h-[var(--pf-topbar-h)] shrink-0 items-center gap-2.5 border-b border-[var(--pf-nav-divider)] px-4">
          {/* لوگوی کامل برند (نشان + کلمه‌نشان «کاربن») بدون قاب رنگی: تصویر
              پس‌زمینه‌ی شفاف دارد و سبزِ برند روی هر دو زمینه‌ی ناوبری —
              روشن و تیره — خوانا می‌ماند. ارتفاع مهار می‌شود و عرض آزاد
              است تا نسبت ۷۲۴×۲۵۶ نشکند. */}
          <img
            src="/brand/logo-wordmark.png"
            alt="کاربن"
            width={724}
            height={256}
            className="h-7 w-auto shrink-0 object-contain"
          />
          <span className="min-w-0">
            <span className="block truncate text-[length:var(--pf-text-sm)] font-[var(--pf-weight-semibold)] text-[var(--pf-nav-text-hover)]">
              پلتفرم یکپارچه سازمان
            </span>
            <span className="block truncate text-[length:var(--pf-text-2xs)] text-[var(--pf-nav-label)]">
              عملیات داخلی سازمان
            </span>
          </span>
        </div>

        <nav className="flex-1 overflow-y-auto px-2.5 py-3">
          <ul className="flex flex-col gap-0.5">
            <li>
              <TopLink to="/app" end icon={ICONS.dashboard} label="داشبورد" onNavigate={onClose} />
            </li>
            <li>
              <TopLink
                to="/app/inbox"
                icon={ICONS.inbox}
                label="کارتابل"
                badge={inboxCount}
                onNavigate={onClose}
              />
            </li>
            <li>
              <TopLink
                to="/app/chat"
                icon={ICONS.chat}
                label="گفتگو"
                badge={typeof chatUnread === 'number' ? chatUnread : undefined}
                dot={chatUnread === 'dot'}
                onNavigate={onClose}
              />
            </li>

            {/*
              ==========================================================
              🔴 تنظیم اعلان‌ها — سطح اول، نه زیرِ «ماژول‌های سیستم».

              ⚠️ ماژول نیست: نه در `MODULES` است و نه زیردرختِ مسیر
              خودش را دارد. گذاشتنش در آن گروه یعنی شمارندهٔ پایینِ
              سایدبار («n ماژول فعال») هم دروغ بگوید.

              🔴 تا پیش از این هیچ راهِ ورودی نداشت جز یک پیوند داخلِ
              صفحهٔ `/app/settings` — یعنی ادمین باید حدس می‌زد که
              تنظیمِ سازمان زیرِ «تنظیماتِ شخصیِ من» پنهان شده. و از
              وقتی ماتریسِ شخصی برداشته شد (‎2026-09-09) آن صفحه عملاً
              فقط یک واسطهٔ بی‌کار بود.

              ⚠️ فقط برای دارندگان مجوز ساخته می‌شود — همان قاعدهٔ
              `canSeeModule`: کسی که هرگز واردش نمی‌شود نباید دکمه‌اش
              را ببیند. `RequirePermission` روی مسیر و `authorize()`
              در Endpoint هم مستقل بررسی می‌کنند (AC-02).
              ==========================================================
            */}
            {can('platform.setting.view') && (
              <li>
                <TopLink
                  to="/app/settings/notification-policy"
                  icon={ICONS.notificationPolicy}
                  label="تنظیم اعلان‌ها"
                  onNavigate={onClose}
                />
              </li>
            )}
          </ul>

          {/* سرگروه ماژول‌ها — قابل جمع شدن تا در نمایشگر کوتاه، ناوبری
              سطح اول همیشه دیده شود. */}
          <button
            type="button"
            onClick={() => setModulesOpen((value) => !value)}
            aria-expanded={modulesOpen}
            aria-controls="pf-nav-modules"
            className="mt-5 mb-1.5 flex w-full items-center justify-between px-2.5 text-[length:var(--pf-text-2xs)] font-[var(--pf-weight-bold)] tracking-wider text-[var(--pf-nav-label)] transition-colors hover:text-[var(--pf-nav-text)]"
          >
            <span>ماژول‌های سیستم</span>
            <svg
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
              className={cn(
                'size-3 shrink-0 transition-transform duration-[var(--pf-duration-fast)]',
                modulesOpen ? 'rotate-180' : 'rotate-0',
              )}
            >
              <path
                d="m3 4.5 3 3 3-3"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <ul id="pf-nav-modules" hidden={!modulesOpen} className="flex flex-col gap-0.5">
            {modules.map((module) => (
              <ModuleGroup
                key={module.key}
                module={module}
                permitted={hasModuleAccess(module.key)}
                isActive={activeKey === module.key}
                isExpanded={expanded.includes(module.key)}
                onToggle={() => toggle(module.key)}
                onNavigate={onClose}
              />
            ))}
          </ul>
        </nav>

        <div className="shrink-0 border-t border-[var(--pf-nav-divider)] px-4 py-3">
          <p className="text-[length:var(--pf-text-2xs)] text-[var(--pf-nav-label)]">
            {/*
              ⚠️ عمداً از رجیستری شمرده می‌شود، نه نوشتهٔ ثابت.

              🔴 اینجا تا امروز «فاز ۴ (پایلوت گزارش کار)» نوشته بود —
              متنی که در فاز ۴ درست بود و بعد از مهاجرتِ هر ده ماژول
              کسی به‌روزش نکرد. کاربر آن را دید و پرسید «مگر تمام
              نشد؟».

              ⚠️ هر متنِ وضعیتی که دستی نوشته شود، دیر یا زود دروغ
              می‌گوید. شمارش خودکار همیشه راست است.
            */}
            نسخه ۱.۰ — {toPersianDigits(MODULES.filter((m) => m.enabled).length)} ماژول فعال
          </p>
        </div>
      </aside>
    </>
  )
}

/* ------------------------------------------------------------------
   گروه ماژول — سرصفحه + بخش‌های داخلی
   ------------------------------------------------------------------ */

function ModuleGroup({
  module,
  permitted,
  isActive,
  isExpanded,
  onToggle,
  onNavigate,
}: {
  module: ModuleDefinition
  permitted: boolean
  isActive: boolean
  isExpanded: boolean
  onToggle: () => void
  onNavigate: () => void
}) {
  const { can, canAny } = useAuth()
  const available = module.enabled && permitted

  /* بخش‌ها هم فیلتر مجوز می‌شوند. برای ماژول غیرفعال فیلتر نمی‌کنیم،
     چون هنوز مجوزی برایش تعریف نشده و هدف صرفاً پیش‌نمایش است. */
  const visibleChildren = module.children.filter((child) => {
    /* بخش‌های `hidden` فقط برای نگاشت آیکون در رجیستری‌اند و مقصد
       ناوبری نیستند — نباید در منو بیایند. */
    if (child.hidden) return false
    if (!available) return true
    if (child.permission) return can(child.permission)
    if (child.anyOf) return canAny(child.anyOf)
    return true
  })

  const hasChildren = visibleChildren.length > 0

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        title={
          !module.enabled
            ? 'این ماژول هنوز به پلتفرم منتقل نشده است'
            : !permitted
              ? 'برای این ماژول دسترسی ندارید'
              : undefined
        }
        className={cn(
          'flex w-full items-center gap-2.5 rounded-[var(--pf-radius)] px-2.5 py-2',
          'text-[length:var(--pf-text-sm)] transition-colors duration-[var(--pf-duration-fast)]',
          /* ماژول فعال = قرص سبز تمام‌عرض با متن تقریباً مشکی */
          isActive
            ? 'bg-[var(--pf-nav-active)] font-[var(--pf-weight-medium)] text-[var(--pf-on-nav-active)]'
            : available
              ? 'text-[var(--pf-nav-text)] hover:bg-[var(--pf-nav-elevated)] hover:text-[var(--pf-nav-text-hover)]'
              : 'text-[var(--pf-nav-label)] hover:bg-[var(--pf-nav-elevated)]',
        )}
      >
        {module.icon}
        <span className="flex-1 truncate text-start">{module.title}</span>

        {!module.enabled && (
          <span
            className={cn(
              /* me-0.5 تا نشان «فاز» به Chevron نچسبد — سایدبار باریک
                 است و بدون آن دو عنصر تداخل می‌کنند */
              'me-0.5 shrink-0 rounded-[var(--pf-radius-sm)] px-1.5 py-0.5 text-[length:var(--pf-text-2xs)]',
              /* 🔴 روی قرص سبز لایه *تیره* می‌نشیند نه روشن.
                 وقتی قرص سبزِ عمیق بود لایه سفید درست بود، ولی حالا
                 قرص خودش روشن است (accent-300) و پردهٔ سفید رویش
                 عملاً دیده نمی‌شود — نشان بی‌شکل می‌شد. متن نشان از
                 --pf-on-nav-active به ارث می‌رسد که تیره است، پس
                 زمینه‌اش هم باید کمی تیره‌تر از خود قرص باشد. */
              isActive
                ? 'bg-black/12'
                : 'bg-[var(--pf-nav-elevated)] text-[var(--pf-nav-label)]',
            )}
          >
            فاز {toPersianDigits(module.phase)}
          </span>
        )}

        {hasChildren && (
          <svg
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
            className={cn(
              'size-3 shrink-0 transition-transform duration-[var(--pf-duration-fast)]',
              isExpanded && 'rotate-180',
            )}
          >
            <path
              d="m3 4.5 3 3 3-3"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>

      {isExpanded && hasChildren && (
        /* خط راهنمای عمودی سمت شروع — در RTL یعنی راست */
        <ul className="my-0.5 ms-4 flex flex-col gap-0.5 border-s border-[var(--pf-nav-divider)] ps-2">
          {visibleChildren.map((child) => (
            <ChildLink
              key={child.path || 'index'}
              module={module}
              child={child}
              available={available}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

function ChildLink({
  module,
  child,
  available,
  onNavigate,
}: {
  module: ModuleDefinition
  child: ModuleChild
  available: boolean
  onNavigate: () => void
}) {
  const to = child.path ? `/app/${module.path}/${child.path}` : `/app/${module.path}`

  const base = cn(
    'block truncate rounded-[var(--pf-radius-sm)] px-2.5 py-1.5',
    'text-[length:var(--pf-text-xs)] transition-colors duration-[var(--pf-duration-fast)]',
  )

  if (!available) {
    return (
      <li>
        <span className={cn(base, 'cursor-not-allowed text-[var(--pf-nav-label)]')}>
          {child.title}
        </span>
      </li>
    )
  }

  return (
    <li>
      <NavLink
        to={to}
        end={!child.path}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            base,
            /* زیرآیتم فعال = متن سبز روی سطح تیره برجسته، نه قرص
               پر — در هر صفحه فقط یک قرص سبز هست. */
            isActive
              ? 'bg-[var(--pf-nav-elevated)] font-[var(--pf-weight-medium)] text-[var(--pf-nav-accent)]'
              : 'text-[var(--pf-nav-text)] hover:bg-[var(--pf-nav-elevated)] hover:text-[var(--pf-nav-text-hover)]',
          )
        }
      >
        {child.title}
      </NavLink>
    </li>
  )
}

/* ------------------------------------------------------------------ */

function TopLink({
  to,
  icon,
  label,
  badge,
  dot,
  end,
  onNavigate,
}: {
  to: string
  icon: ReactNode
  label: string
  badge?: number
  /** «چیزی تازه هست» بی‌شمارش — Rocket.Chat برای پیام‌های بی‌اشاره همین را می‌دهد. */
  dot?: boolean
  end?: boolean
  onNavigate: () => void
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-[var(--pf-radius)] px-2.5 py-2',
          'text-[length:var(--pf-text-sm)] transition-colors duration-[var(--pf-duration-fast)]',
          /* آیتم فعال سطح اول = قرص سبز تمام‌عرض */
          isActive
            ? 'bg-[var(--pf-nav-active)] font-[var(--pf-weight-medium)] text-[var(--pf-on-nav-active)]'
            : 'text-[var(--pf-nav-text)] hover:bg-[var(--pf-nav-elevated)] hover:text-[var(--pf-nav-text-hover)]',
        )
      }
    >
      {icon}
      <span className="flex-1 truncate">{label}</span>
      {badge != null && <CountBadge count={badge} tone="nav-danger" />}
      {dot && !badge && (
        <span
          role="status"
          aria-label="پیام خوانده‌نشده"
          className="size-2 shrink-0 rounded-[var(--pf-radius-full)] bg-[var(--pf-nav-danger)]"
        />
      )}
    </NavLink>
  )
}
